import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import logoFull from "../assets/tenditaly-logo-full.png";

export default function Login() {
  const { loginPassword } = useAuth();
  const nav = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "register") {
        await api.post("/auth/register", { name, email, password });
        toast.success("Registrazione inviata! Il titolare riceverà un'email e attiverà il tuo accesso.");
        setMode("login"); setName("");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email });
        toast.success("Se l'email è registrata riceverai un link per reimpostare la password.");
        setMode("login");
      } else {
        await loginPassword(email, password);
        nav("/");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (mode === "register" ? "Registrazione non riuscita" : mode === "forgot" ? "Richiesta non riuscita" : "Accesso non riuscito"));
    } finally { setBusy(false); }
  };

  const google = () => {
    const redirectUrl = window.location.origin + "/";
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const title = mode === "register" ? "Registrati" : mode === "forgot" ? "Recupera password" : "Accedi";
  const cta = busy ? "Attendi…" : mode === "register" ? "Invia richiesta" : mode === "forgot" ? "Invia link di recupero" : "Entra";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex flex-col justify-between bg-ink p-12 relative overflow-hidden">
        <div className="relative z-10">
          <div className="bg-white/95 rounded-2xl inline-flex p-5"><img src={logoFull} alt="TENDItaly" className="h-20 object-contain" /></div>
        </div>
        <div className="relative z-10 text-white max-w-md">
          <h1 className="font-head text-4xl font-extrabold leading-tight">Il gestionale della tua azienda</h1>
          <p className="text-white/60 mt-4 text-lg">Clienti, preventivi, sopralluoghi, lavori, calendario con foto, incassi e WhatsApp — in un unico posto.</p>
        </div>
        <div className="absolute -right-20 -bottom-20 w-96 h-96 rounded-full bg-brand/20 blur-3xl" />
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex justify-center mb-8"><img src={logoFull} alt="TENDItaly" className="h-16 object-contain" /></div>
          <h2 className="font-head text-2xl font-bold text-center">{title}</h2>
          <p className="text-sm text-muted-foreground text-center mb-8">Gestionale TENDItaly di Elia Fiorenzani</p>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label htmlFor="name">Nome e cognome</Label>
                <Input id="name" data-testid="register-name" value={name} onChange={(e) => setName(e.target.value)} required />
              </div>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@tenditaly.it" required />
            </div>
            {mode !== "forgot" && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  {mode === "login" && (
                    <button type="button" data-testid="forgot-password-link" onClick={() => setMode("forgot")} className="text-xs text-muted-foreground hover:text-brand underline">
                      Password dimenticata?
                    </button>
                  )}
                </div>
                <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
            )}
            {mode === "forgot" && (
              <p className="text-xs text-muted-foreground">Inserisci la tua email: ti invieremo un link per creare una nuova password.</p>
            )}
            <Button type="submit" data-testid={mode === "register" ? "register-submit" : mode === "forgot" ? "forgot-submit" : "login-submit"} disabled={busy} className="w-full bg-brand hover:bg-[#A8472F] text-white">
              {cta}
            </Button>
          </form>

          {mode === "forgot" ? (
            <p className="text-sm text-center mt-4 text-muted-foreground">
              <button type="button" data-testid="back-to-login" onClick={() => setMode("login")} className="text-brand font-medium underline">
                Torna all'accesso
              </button>
            </p>
          ) : (
            <p className="text-sm text-center mt-4 text-muted-foreground">
              {mode === "register" ? "Hai già un accesso?" : "Non hai un accesso?"}{" "}
              <button type="button" data-testid="toggle-register" onClick={() => setMode(mode === "register" ? "login" : "register")} className="text-brand font-medium underline">
                {mode === "register" ? "Accedi" : "Registrati"}
              </button>
            </p>
          )}

          {mode !== "forgot" && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px bg-border flex-1" />
                <span className="text-xs text-muted-foreground">oppure</span>
                <div className="h-px bg-border flex-1" />
              </div>
              <Button type="button" variant="outline" onClick={google} data-testid="login-google" className="w-full">
                Accedi con Google
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
